import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import FeatureSection from '../common/FeatureSection';
import ImageLayout from '../common/ImageLayout';
import LatestBlogs from './LatestBlogs';
import { useDispatch, useSelector } from 'react-redux';
import { setBannerVisibility } from '../../redux/counterSlice';
import { RootState } from '../../redux/store';

interface Testimonial {
  quote: string;
  author: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      "My 8yr old son just earned a Gold Dragon Master Medal in the Functions in Python course, and I've never seen him so proud! He's already started the next course!",
    author: 'Lisa M., Mom of Theodore (8yr old)',
  },
  {
    quote:
      "As a parent with zero coding experience, I love that the My Code Dragon's AI tutor thing can guide my daughter through the coding activities, this stuff goes over my head. Whenever she gets stuck and has an error with her code, it helps explain what the code is doing and how to fix it, in a super easy to understand way. She is having fun and learning a lot, so I just wanted to say thank you.",
    author: 'Sarah K., Mom of Tianna (14yr old)',
  },
  {
    quote:
      "My Code Dragon is awesome, we were initially looking for in person Python coding classes for our 11yr old who is really into coding, but with our kids sports and other activities we couldn't fit it into our schedule. Love that my son can log on and start coding during the week when he has some down time, he really likes it so far.",
    author: 'Chris C., Dad of James (11yr old)',
  },
];

const HomeSection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isBannerVisible = useSelector(
    (state: RootState) => state.app.banner.isVisible,
  );
  const [bannerActive, setBannerActive] = React.useState(true);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on initial load
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // useEffect(() => {
  //   if (isBannerVisible) {
  //     setTimeout(() => {
  //       setBannerActive(true);
  //     }, 100);
  //   }
  // }, [isBannerVisible]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => {
        const increment = isMobile ? 1 : 2;
        return prevIndex + increment >= testimonials.length
          ? 0
          : prevIndex + increment;
      });
    }, 5000); // Change testimonials every 5 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  const handleBannerClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBannerActive(false);

    // setTimeout(() => {
    //   dispatch(setBannerVisibility(false)); // Dispatch action to update Redux state
    // }, 500);
  };

  const handleBannerClick = () => {
    navigate('/signup?subscriptionType=1');
    setBannerActive(false);

    // setTimeout(() => {
    //   dispatch(setBannerVisibility(false)); // Dispatch action to update Redux state
    // }, 500);
  };

  const handlePrevTestimonials = () => {
    setCurrentTestimonialIndex((prevIndex) => {
      const decrement = isMobile ? 1 : 2;
      const newIndex = prevIndex - decrement;
      return newIndex < 0
        ? testimonials.length -
            (testimonials.length % (isMobile ? 1 : 2) || (isMobile ? 1 : 2))
        : newIndex;
    });
  };

  const handleNextTestimonials = () => {
    setCurrentTestimonialIndex((prevIndex) => {
      const increment = isMobile ? 1 : 2;
      const newIndex = prevIndex + increment;
      return newIndex >= testimonials.length ? 0 : newIndex;
    });
  };

  const getVisibleTestimonials = () => {
    if (isMobile) {
      return [testimonials[currentTestimonialIndex]];
    } else {
      const first = testimonials[currentTestimonialIndex];
      const secondIndex = (currentTestimonialIndex + 1) % testimonials.length;
      const second = testimonials[secondIndex];
      return [first, second];
    }
  };

  return (
    <div>
      <ImageLayout />

      <div className="relative flex flex-col items-center justify-center p-8 py-16">
        <button
          onClick={handlePrevTestimonials}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#023f81] hover:bg-[#034ea0] transition-colors border border-white/30"
          aria-label="Previous testimonials"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        <div className="flex flex-col md:flex-row gap-6 max-w-7xl mx-auto w-full">
          {getVisibleTestimonials().map((testimonial, index) => (
            <div
              key={`${currentTestimonialIndex}-${index}`}
              className="flex-1 bg-white rounded-lg p-6 transition-all duration-500 border-t-4 border-t-[#023f81] shadow-[0_0_15px_rgba(0,0,0,0.2)]"
            >
              <p className="text-sm md:text-base text-gray-700 italic mb-4">
                "{testimonial.quote}"
              </p>
              <div className="text-sm text-gray-600">
                <p className="font-semibold">- {testimonial.author}</p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleNextTestimonials}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#023f81] hover:bg-[#034ea0] transition-colors border border-white/30"
          aria-label="Next testimonials"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>

      {bannerActive && (
        <div
          className={`fixed bottom-0 right-0 mb-4 mr-4 transform transition-all duration-500 ease-in-out ${
            bannerActive ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ zIndex: 50 }}
        >
          <div
            className="relative w-[320px] bg-gradient-to-br from-[#023f81] to-[#6FD6FF] 
             text-center p-5 rounded-lg shadow-2xl cursor-pointer transition-transform duration-300 
             hover:scale-105"
          >
            <X
              className="absolute top-2 right-2 h-6 w-6 text-white cursor-pointer hover:text-gray-200 transition"
              onClick={(e) => handleBannerClose(e)}
            />

            <h2 className="text-2xl font-bold text-white mb-3">
              Unlock a 3-Day Free Trial of My Code Dragon!
            </h2>

            <p className="text-base text-white mb-3">
              Discover how fun coding can be when learning is hands-on,
              interactive, and supported by a real-time My Code Dragon AI.
            </p>

            <button
              className="bg-white text-[#023f81] font-semibold py-2 px-4 rounded-lg 
              hover:bg-gray-100 transition-colors mb-3 w-full"
              onClick={() => handleBannerClick()}
            >
              Start Your Free Trial Now
            </button>

            <p className="text-sm text-white/90 italic">
              Join thousands of families investing in the future by developing
              essential coding skills!
            </p>
          </div>
        </div>
      )}

      <FeatureSection />
      <LatestBlogs />
    </div>
  );
};

export default HomeSection;
